import React, {FC, useContext} from 'react';
import {
  BreakpointSizes,
  CommonFeatureServices,
  getModelEngineOptions,
  getTrimSelectOptionGroups,
  ImagePlaceholder,
  ModelEfficiencyData,
  ModelSelection,
  ResponsiveImage,
  useDevConfig,
  useFeatureService,
  StyledModelSelectionArea,
  MobileViewConditionalRenderer,
  useGetAppDataContentFlag,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {useTranslation} from '../use-translation';
import {
  BffTrimData,
  ChargeTimeTrimModel,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {ChargeTimeCalculationFormFields} from '../use-charge-calculation-form';
import {useTrims} from '../use-trims';
import {ThemeContext} from 'styled-components';
import {useTenantConfiguration} from '../use-tenant-configuration';

interface CarSelectionSectionProps {
  selectedTrim: BffTrimData<ChargeTimeTrimModel>;
  selectedModel: ChargeTimeTrimModel;
  modelTypeField: ChargeTimeCalculationFormFields['modelTypeId'];
  modelEngineField: ChargeTimeCalculationFormFields['modelEngineId'];
}

export const CarSelectionSection: FC<CarSelectionSectionProps> = React.memo(
  ({
    selectedModel,
    selectedTrim,
    modelTypeField,
    modelEngineField,
  }: CarSelectionSectionProps) => {
    const {modelCombinedDropdowns} = useDevConfig();
    const translations = useTranslation();
    const chargeTimeTenantConfig = useTenantConfiguration();
    const hideVehicleImage = useGetAppDataContentFlag('hideVehicleImage');

    const trims = useTrims();
    const logger = useFeatureService(CommonFeatureServices.s2Logger);

    const trimSelectOptions = React.useMemo(() => {
      return getTrimSelectOptionGroups(
        trims,
        chargeTimeTenantConfig.carSelectionSection.trimSelection.trimGrouping,
        modelCombinedDropdowns,
        logger
      );
    }, [trims, chargeTimeTenantConfig, modelCombinedDropdowns]);

    const modelEngineOptions = React.useMemo(() => {
      return getModelEngineOptions(selectedTrim, logger);
    }, [selectedTrim]);

    const {Breakpoints} = useContext(ThemeContext).internal;
    const {customOneColumnBreakpoint} = useContext(ThemeContext).brand.spacing;

    /* The code and comments for the image size is copied from  packages/range-calc-feature-app/src/components/car-selection-section.tsx */
    // sizes in columns according to layout (each column is 100vw / 24 wide)
    const vehicleImageBreakpointSizes: BreakpointSizes = {
      [Breakpoints.default]: 'calc((100vw / 24) * 20)',
      [Breakpoints.b960]: 'calc((100vw / 24) * 8)',
      // calculated based on 8 columns for grid max size of 2560px
      [Breakpoints.b2560]: '853px',
    };

    const oneColumnImageBreakpointSizes: BreakpointSizes = {
      [Breakpoints.default]: '1920px',
      [Breakpoints.b2560]: '1920px',
    };

    const mainImage = selectedTrim.assets.images.main;

    const modelHeadline = translations.carSelectionSection.modelHeading
      .replace('%%carline%%', selectedModel.carlineName)
      .replace('%%salesgroup%%', selectedModel.salesgroupName)
      .replace('%%trim%%', selectedTrim.trimId);

    const includeDisclaimerInEfficiencyLayer =
      chargeTimeTenantConfig.efficiency.includeDisclaimerInEfficiencyLayer ??
      false;

    return (
      <section
        aria-label={
          translations.carSelectionSection.modelAndMotorSelection.ariaLabel
        }
      >
        <StyledModelSelectionArea hideBottomPadding={hideVehicleImage}>
          <ModelSelection
            modelHeadline={modelHeadline}
            modelTypeField={modelTypeField}
            modelTypeSelectGroups={trimSelectOptions}
            modelEngineField={modelEngineField}
            modelEngineSelectOptions={modelEngineOptions}
          />
        </StyledModelSelectionArea>
        <MobileViewConditionalRenderer hideWhenMobileView={hideVehicleImage}>
          {mainImage && (
            <ImagePlaceholder imageRatio={mainImage.aspectRatio}>
              <ResponsiveImage
                src={mainImage.url}
                srcVariants={mainImage.sizeUrls}
                breakpointSizes={
                  customOneColumnBreakpoint
                    ? oneColumnImageBreakpointSizes
                    : vehicleImageBreakpointSizes
                }
                alt={selectedModel.longName}
              />
            </ImagePlaceholder>
          )}
          {selectedModel && selectedModel.efficiency && (
            <ModelEfficiencyData
              modelName={selectedModel.longName}
              efficiencyData={selectedModel.efficiency}
              disclaimerReferenceLabelTranslation={
                translations.global.disclaimerReferenceLabel
              }
              closeButtonLabel={
                translations.global.educationalInfoLayer.closeButtonLabel
              }
              efficiencyTranslations={translations.global.efficiency}
              includeDisclaimerInEfficiencyLayer={
                includeDisclaimerInEfficiencyLayer
              }
              isEfficiencyClassMandatory={
                chargeTimeTenantConfig.featureFlags?.isEfficiencyClassMandatory
              }
            />
          )}
        </MobileViewConditionalRenderer>
      </section>
    );
  }
);

CarSelectionSection.displayName = 'CarSelectionSection';
